import { incApi } from 'api/inc-api';
import { Paging } from 'api/inc-api.types';
import {
  generateInvalidates,
  generateProvides,
  generateProvidesList,
} from 'util/generateTags';
import {
  GiftCard,
  ViewGiftCard,
  adjustGiftCardBalanceResult,
  zeroGiftCardBalance,
  Integration,
  LocationConfig,
  UpdateGiftCardConfiguration,
  UpdateLocationConfiguration,
  GiftCardTransactions,
  GiftCardEvents,
  GiftCardBalanceReportResponseType,
  ListGiftCardImportsParams,
  ImportCardResponse,
  DeleteGiftCardImportResponse,
  DeleteGiftCardImportRequest,
  ImportCardRequest,
  SearchGiftCardAccountBatchSummaryResponseDTO,
  GiftCardAccountBatchSummaryDTO,
} from './giftcard-service.types';
import { transformGiftCardRequest } from './giftcard-service.utils';
import { CreateCompCardArgs, CreateCompCardResult } from './giftcard.types';
import { getClientId } from 'util/client-id';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    sendGiftCardTransactionReceipt: build.query<
      void,
      { id: string; extTransactionId: string; fulfilledNotification: string }
    >({
      query: ({ id, extTransactionId }) => ({
        url: `incentivio-giftcard-service/giftcard/transaction/${id}/sendreceipt`,
        params: { extTransactionId },
      }),
      keepUnusedDataFor: 0,
    }),

    listGiftCards: build.query<{ content: GiftCard[] } & Paging, any>({
      query: params => ({
        url: `incentivio-giftcard-service/cards/search`,
        method: 'GET',
        params: transformGiftCardRequest(params),
      }),
      providesTags: generateProvidesList(
        result => result?.giftCards,
        element => element.cardIdentifier,
        'GiftCard',
      ),
    }),
    getGiftCard: build.query<ViewGiftCard, string>({
      query: cardNumber => ({
        url: `incentivio-giftcard-service/card/view`,
        method: 'GET',
        params: { cardNumber: cardNumber },
      }),
      transformResponse: response => {
        return { ...response, recipientEmail: response?.recipient?.email };
      },
    }),
    adjustGiftCardBalance: build.mutation<adjustGiftCardBalanceResult, any>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/admin-add-value`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards', 'GiftCardTransactions'],
    }),

    removeValueGiftCardBalance: build.mutation<
      adjustGiftCardBalanceResult,
      any
    >({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/remove-value`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards', 'GiftCardTransactions'],
    }),

    setZeroGiftCardBalance: build.mutation<zeroGiftCardBalance, any>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/reset-balance`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards', 'GiftCardTransactions'],
    }),
    activateGiftCard: build.mutation<zeroGiftCardBalance, any>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/activate`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards', 'GiftCardTransactions'],
    }),
    deactivateGiftCard: build.mutation<zeroGiftCardBalance, any>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/deactivate`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards', 'GiftCardTransactions'],
    }),
    resendEmailGiftCard: build.mutation<zeroGiftCardBalance, any>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card/resendemail`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['GiftCards'],
    }),

    createCompCard: build.mutation<CreateCompCardResult, CreateCompCardArgs>({
      query: body => {
        return {
          url: `incentivio-giftcard-service/card`,
          method: 'POST',
          body: body,
        };
      },
      invalidatesTags: ['GiftCards'],
    }),

    getGiftCardIntegration: build.query<Integration, void>({
      query: () =>
        `incentivio-giftcard-service/client/${getClientId()}/giftcard/client-config`,
      providesTags: ['Integrations'],
      transformResponse: (response: Integration) => {
        return response;
      },
    }),

    getLocationConfigGiftCardIntegration: build.query<LocationConfig, void>({
      query: params => ({
        url: `incentivio-giftcard-service/giftcard/locations-config`,
        params,
      }),
      providesTags: ['LocationsConfigured'],
      transformResponse: (response: LocationConfig) => {
        return response;
      },
    }),

    updateGiftCardConfig: build.mutation<void, UpdateGiftCardConfiguration>({
      query: body => ({
        url: `incentivio-giftcard-service/giftcard/client-config/update`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['Integrations'],
    }),

    updateLocationConfig: build.mutation<void, UpdateLocationConfiguration>({
      query: body => ({
        url: `incentivio-giftcard-service/giftcard/location-config/update`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['LocationsConfigured'],
    }),

    getGiftCardTransactions: build.query<
      { content: GiftCardTransactions } & Paging,
      any
    >({
      query: params => ({
        url: `incentivio-giftcard-service/card/transactions/view`,
        method: 'GET',
        params: {
          ...params,
        },
      }),
      providesTags: generateProvidesList(
        result => result?.giftCardTransactionList,
        element => element.transactionDate,
        'GiftCardTransaction',
      ),
    }),

    getGiftCardEvents: build.query<GiftCardEvents, string>({
      query: cardNumber => ({
        url: `incentivio-giftcard-service/card/events/view`,
        method: 'GET',
        params: { cardNumber: cardNumber },
      }),
    }),

    exportGiftCardBalanceReportCSV: build.query<
      GiftCardBalanceReportResponseType,
      { liableasofdate: string }
    >({
      query: params => ({
        url: `incentivio-giftcard-service/clients/${getClientId()}/balance-report/export`,
        params,
      }),
    }),

    listGiftCardImports: build.query<
      SearchGiftCardAccountBatchSummaryResponseDTO,
      ListGiftCardImportsParams
    >({
      query: params => ({
        url: `incentivio-giftcard-service/cards/batch-summary`,
        method: 'GET',
        params,
      }),
      providesTags: generateProvidesList(
        result => result?.content,
        element => element.id,
        'GiftCardImport',
      ),
    }),

    getGiftCardImport: build.query<GiftCardAccountBatchSummaryDTO, string>({
      query: id => ({
        url: `incentivio-giftcard-service/cards/batch-summary/${id}`,
        method: 'GET',
      }),
      providesTags: generateProvides(result => result.id, 'GiftCardImport'),
    }),

    deleteGiftCardImport: build.mutation<
      DeleteGiftCardImportResponse,
      DeleteGiftCardImportRequest
    >({
      query: body => ({
        url: `incentivio-giftcard-service/cards/import/${body.importBatchId}/remove`,
        method: 'POST',
        body,
      }),
      invalidatesTags: generateInvalidates(
        ({ importBatchId }) => importBatchId,
        'GiftCardImport',
      ),
    }),

    uploadGiftCardImport: build.mutation<ImportCardResponse, ImportCardRequest>(
      {
        query: body => ({
          url: `incentivio-giftcard-service/cards/import`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['GiftCardImport'],
      },
    ),
  }),
});

export const {
  useSendGiftCardTransactionReceiptQuery,
  useListGiftCardsQuery,
  useGetGiftCardQuery,
  useAdjustGiftCardBalanceMutation,
  useSetZeroGiftCardBalanceMutation,
  useRemoveValueGiftCardBalanceMutation,
  useActivateGiftCardMutation,
  useDeactivateGiftCardMutation,
  useResendEmailGiftCardMutation,
  useCreateCompCardMutation,
  useUpdateLocationConfigMutation,
  useUpdateGiftCardConfigMutation,
  useGetGiftCardIntegrationQuery,
  useGetLocationConfigGiftCardIntegrationQuery,
  useGetGiftCardTransactionsQuery,
  useGetGiftCardEventsQuery,
  useLazyGetGiftCardQuery,
  useLazyExportGiftCardBalanceReportCSVQuery,
  useListGiftCardImportsQuery,
  useGetGiftCardImportQuery,
  useDeleteGiftCardImportMutation,
  useUploadGiftCardImportMutation,
} = endpoints;
